// src/pages/settings/AddUser.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import Header from "../../components/Header";
import API_BASE_URL from "../../config";
import { Modal, Button } from "react-bootstrap";
import { Link } from "react-router-dom";

const AddUser = () => {
  useEffect(() => {
        document.title = "Add User | Webyappy";
      }, []);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    role: "",
    profilePic: null,
  });

  const [errors, setErrors] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState("");
  const [responseEmail, setResponseEmail] = useState("");

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "profilePic") {
      const file = files[0];
      if (file) {
        const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
        if (!allowedTypes.includes(file.type)) {
          alert("Only JPG, JPEG, and PNG formats are allowed.");
          return;
        }
        if (file.size > 2 * 1024 * 1024) {
          alert("File size should not exceed 2MB.");
          return;
        }
        setForm({ ...form, profilePic: file });
      }
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const checkDuplicate = async (field, value) => {
    try {
      const res = await axios.post(`${API_BASE_URL}/auth/check_duplicate`, {
        field,
        value,
      });
      return res.data.exists;
    } catch (err) {
      console.error(err);
      return false;
    }
  };

  const checkFieldDuplicate = async (field, value) => {
    if (!value) return;
    const exists = await checkDuplicate(field, value);
    if (exists) {
      setErrors((prev) => ({
        ...prev,
        [field]: `${field === "email" ? "Email" : "Phone"} already exists`,
      }));
    } else {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validate = async () => {
    const errs = {};
    if (!form.name) errs.name = "Full name is required";
    if (!form.email) errs.email = "Email is required";
    else if (await checkDuplicate("email", form.email))
      errs.email = "Email already exists";

    if (!form.phone) errs.phone = "Phone is required";
    else if (await checkDuplicate("phone", form.phone))
      errs.phone = "Phone already exists";

    if (!form.role) errs.role = "Role is required";

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return; // Prevent double-click spam

    setIsSubmitting(true); // Start loader

    const isValid = await validate();
    if (!isValid) {
      setIsSubmitting(false);
      return;
    }

    const data = new FormData();
    data.append("name", form.name);
    data.append("email", form.email);
    data.append("phone", form.phone);
    data.append("role", form.role);
    data.append("profilePic", form.profilePic);

    try {
      const res = await axios.post(`${API_BASE_URL}/auth/add_user`, data, {
        withCredentials: true,
      });

      if (res.data.status === "success") {
        setResponseEmail(res.data.email);
        setGeneratedPassword(res.data.temp_password);
        setShowModal(true);
        setForm({
          name: "",
          email: "",
          phone: "",
          role: "",
          profilePic: null,
        });
      } else {
        console.error("🛑 Submission failed:", res.data);
      }
    } catch (err) {
      console.error("🔥 Server error:", err);
    } finally {
      setIsSubmitting(false); // End loader
    }
  };

  return (
    <>
      <Header />
      <div style={{ paddingTop: "70px", paddingBottom: "70px" }}>
      <div className="container-fluid px-3 py-3">
        {/* Breadcrumb + Buttons */}
        <div className="d-flex align-items-center justify-content-between mb-4">
          <ul className="breadcrumb breadcrumb-transparent breadcrumb-dot font-weight-bold mb-0 p-0">
            <li className="breadcrumb-item">
              <Link to="/home" className="text-muted">
                Home
              </Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/pages/settings/Settings" className="text-muted">
                Settings
              </Link>
            </li>
            <li className="breadcrumb-item">
              <span className="text-dark font-weight-bold">Add User</span>
            </li>
          </ul>
        </div>

        {/* White Card Container */}
        <div className="card card-custom shadow-sm">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-2">
              <h5 className="font-weight-bold mb-0">Add User</h5>
              <div className="d-flex gap-2">
                <Link to="/pages/settings/Settings" className="btn btn-light">
                  <i className="fas fa-arrow-left fa-xs"></i> Back
                </Link>
                <button
                  className="btn btn-primary"
                  type="submit"
                  form="addUserForm"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm mr-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check fa-xs mr-1"></i> Submit
                    </>
                  )}
                </button>
              </div>
            </div>

            <form
              id="addUserForm"
              onSubmit={handleSubmit}
              encType="multipart/form-data"
            >
              <div className="row">
                <div className="form-group col-md-6 mb-3">
                  <label className="small">
                    Full Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="name"
                    placeholder="Type Here..."
                    value={form.name}
                    onChange={handleChange}
                  />
                  {errors.name && (
                    <small className="text-danger">{errors.name}</small>
                  )}
                </div>

                <div className="form-group col-md-6 mb-3">
                  <label className="small">
                    Email Address <span className="text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    placeholder="Type Here..."
                    value={form.email}
                    onChange={handleChange}
                    onBlur={() => checkFieldDuplicate("email", form.email)}
                  />

                  {errors.email && (
                    <small className="text-danger">{errors.email}</small>
                  )}
                </div>

                <div className="form-group col-md-6 mb-3">
                  <label className="small">
                    Phone Number <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="phone"
                    placeholder="Type Here..."
                    value={form.phone}
                    onChange={handleChange}
                    onBlur={() => checkFieldDuplicate("phone", form.phone)}
                  />

                  {errors.phone && (
                    <small className="text-danger">{errors.phone}</small>
                  )}
                </div>

                <div className="form-group col-md-6 mb-3">
                  <label className="small">
                    Role <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    name="role"
                    value={form.role}
                    onChange={handleChange}
                  >
                    <option value="">Select Role</option>
                    <option>Superadmin</option>
                    <option>System User</option>
                    <option>Sales</option>
                  </select>
                  {errors.role && (
                    <small className="text-danger">{errors.role}</small>
                  )}
                </div>

                <div className="form-group col-md-6 mb-3">
                  <label className="small">Profile Picture</label>
                  <input
                    type="file"
                    className="form-control"
                    name="profilePic"
                    accept="image/*"
                    onChange={handleChange}
                  />
                  <p className="form-text text-danger" style={{ fontSize: "11px" }}>
                    JPG, JPEG, or PNG only. Max size: 2MB.
                  </p>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Success Modal */}
        <Modal show={showModal} onHide={() => setShowModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>User Added Successfully</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              Email: <strong>{responseEmail}</strong>
            </p>
            <p>
              Temporary Password: <strong>{generatedPassword}</strong>
            </p>
            <hr />
            <p>What do you want to do next?</p>
            <div className="d-flex flex-wrap gap-2">
              <Button
                variant="outline-dark"
                onClick={() => window.location.reload()}
              >
                Add Another User
              </Button>

              <Link
                to="/pages/settings/ViewUser"
                className="btn btn-outline-primary"
              >
                View/Edit Users
              </Link>

              <Link
                to="/pages/settings/AssignMicroapp"
                className="btn btn-outline-secondary"
              >
                Assign Microapp
              </Link>

              <Link
                to="/pages/settings/Settings"
                className="btn btn-outline-success"
              >
                Settings
              </Link>

              <Link to="/home" className="btn btn-outline-warning">
                Home
              </Link>
            </div>
          </Modal.Body>
        </Modal>
      </div>
      
      </div>
      
    </>
  );
};

export default AddUser;
